class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // The rendered contents are authoritative, including after app updates.
    this.classList.toggle('is-empty', Boolean(this.querySelector('.drawer__inner-empty')));
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('animate');
    requestAnimationFrame(() => {
      this.classList.add('active');
      // Visibility transitions begin after this frame; hidden elements cannot receive focus.
      clearTimeout(this.focusTimer);
      this.focusTimer = setTimeout(() => {
        if (!this.classList.contains('active')) return;
        const container = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : this.querySelector('.drawer__inner');
        trapFocus(container, container.querySelector('.drawer__close'));
      }, 50);
    });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    clearTimeout(this.focusTimer);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  applySections(sections) {
    const drawerDoc = this.getSectionDOM(sections?.['cart-drawer'], 'cart-drawer');
    const content = drawerDoc?.querySelector('#CartDrawer');
    const bubble = this.getSectionDOM(sections?.['cart-icon-bubble']);
    const bubbleTarget = document.getElementById('cart-icon-bubble');
    // Validate every fragment before replacing anything. Shopify can return null sections.
    if (!content?.querySelector('.drawer__inner') || !bubble || !bubbleTarget) throw new Error('Incomplete cart sections');
    if (!content.querySelector('.drawer__inner-empty, .cart-item')) throw new Error('Empty cart fragment');
    this.refreshId = (this.refreshId || 0) + 1;
    this.querySelector('#CartDrawer').innerHTML = content.innerHTML;
    this.classList.toggle('is-empty', drawerDoc.classList.contains('is-empty'));
    bubbleTarget.innerHTML = bubble.innerHTML;
  }

  async refresh() {
    const requestId = this.refreshId = (this.refreshId || 0) + 1;
    const url = new URL(routes.cart_url, window.location.origin);
    url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart refresh failed');
    const sections = await response.json();
    if (requestId === this.refreshId) this.applySections(sections);
  }

  async renderContents(parsedState) {
    this.productId = parsedState.id;
    // Invalidate older external refreshes before committing this mutation response.
    this.refreshId = (this.refreshId || 0) + 1;
    try {
      this.applySections(parsedState.sections);
    } catch (error) {
      try { await this.refresh(); }
      catch (refreshError) { window.location.assign(routes.cart_url); return; }
    }
    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onCartUpdate(event) {
    // ProductForm renders the same response; an extra fetch can race with that render.
    if (event?.source === 'product-form') return;
    document.querySelector('cart-drawer')?.refresh().catch(() => {});
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
